import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import "./App.css";
import { initI18n } from "./utils/i18n";
import { TranslationProvider } from "./hooks/useTranslation";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ContactPopup from "./components/ContactPopup";
import AudioPlayer from "./components/AudioPlayer";
import useVisitorTracking from "./hooks/useVisitorTracking";
import Home from "./pages/Home";
import ListenLive from "./pages/ListenLive";
import Programs from "./pages/Programs";
import Impact from "./pages/Impact";
import Donate from "./pages/Donate";
import MajorGiftsPage from "./pages/MajorGiftsPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Reports from "./pages/Reports";
import CommunityFoundationReport from "./pages/CommunityFoundationReport";
import ProgramsLineup from "./pages/ProgramsLineup";
import ChurchPartners from "./pages/ChurchPartners";
import MediaGallery from "./pages/MediaGallery";
import PresentersDashboard from "./pages/PresentersDashboard";
import Volunteer from "./pages/Volunteer";
import Visitors from "./pages/Visitors";
import Donations from "./pages/Donations";
import ProjectDetail from "./pages/ProjectDetail";
import ProgramAssistant from "./pages/ProgramAssistant";
import CRM from "./pages/CRM";
import Dashboard from "./pages/Dashboard";
import Launch from "./pages/Launch";
import Clocks from "./pages/Clocks";
import Gallery from "./pages/Gallery";
import Podcast from "./pages/Podcast";
import WeatherDashboard from "./pages/WeatherDashboard";
import ReportingLogin from "./pages/ReportingLogin";
import ReportingDashboard from "./pages/ReportingDashboard";
import EditorialDashboard from "./pages/EditorialDashboard";
import ReportingNews from "./pages/ReportingNews";
import FamilySignup from "./pages/FamilySignup";
import AMinuteWithGod from "./pages/AMinuteWithGod";
import AMinuteWithGodLanguage from "./pages/AMinuteWithGodLanguage";
import MobileScanner from "./pages/MobileScanner";
import OnlineScoutDashboard from "./pages/OnlineScoutDashboard";
import GiveNow from "./pages/GiveNow";
import Staff from "./pages/Staff";
import ImpactStories from "./pages/ImpactStories";
import KiooCRMHub from "./pages/KiooCRMHub";
import KissiLearn from "./pages/KissiLearn";
import MobileAppShell from "./mobile/MobileAppShell";
import MobileHome from "./mobile/pages/MobileHome";
import MobileSchedule from "./mobile/pages/MobileSchedule";
import MobileMessage from "./mobile/pages/MobileMessage";
import MobileContact from "./mobile/pages/MobileContact";
import MobilePrayer from "./mobile/pages/MobilePrayer";
import VoiceInbox from "./mobile/pages/VoiceInbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);

  // Toggle a body class while the fixed audio player is visible so pages get
  // enough bottom padding on mobile to keep content clear of the player bar.
  useEffect(() => {
    if (isPlayerVisible) {
      document.body.classList.add('player-open');
    } else {
      document.body.classList.remove('player-open');
    }
    return () => document.body.classList.remove('player-open');
  }, [isPlayerVisible]);

  // Initialize Google Analytics
  useEffect(() => {
    // Initialize new i18n system
    initI18n();
    
    // Initialize Google Analytics
    const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    if (gaId && gaId !== 'G-PLACEHOLDER123') {
      ReactGA.initialize(gaId);
      console.log('Google Analytics initialized with ID:', gaId);
    } else {
      console.log('Google Analytics not initialized - please set REACT_APP_GA4_MEASUREMENT_ID');
    }
    
    // Make contact popup function global
    window.openContactPopup = () => setIsContactPopupOpen(true);
  }, []);

  // Component for tracking page views with GA
  const GoogleAnalyticsTracker = () => {
    const location = useLocation();
    
    useEffect(() => {
      const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
      if (gaId && gaId !== 'G-PLACEHOLDER123') {
        ReactGA.send({ 
          hitType: 'pageview', 
          page: location.pathname + location.search 
        });
      }
    }, [location]);

    return null;
  };

  // Create a component to handle visitor tracking inside Router
  const VisitorTracker = () => {
    useVisitorTracking();
    return null;
  };

  return (
    <HelmetProvider>
      <TranslationProvider>
        <div className="App min-h-screen bg-white">
          <Router>
          <GoogleAnalyticsTracker />
          <VisitorTracker />
          
          <Routes>
            {/* Standalone Scanner App - No Header/Footer */}
            <Route path="/scanner" element={<MobileScanner />} />
            
            {/* Online Scout Dashboard - CRM Tool */}
            <Route path="/crm/scout" element={<OnlineScoutDashboard />} />

            {/* Mobile App (app.kiooradio.org / kiooradio.org/app) — standalone shell, own PWA */}
            <Route path="/app" element={<MobileAppShell />}>
              <Route index element={<MobileHome />} />
              <Route path="programs" element={<MobileSchedule />} />
              <Route path="message" element={<MobileMessage />} />
              <Route path="prayer" element={<MobilePrayer />} />
              <Route path="contact" element={<MobileContact />} />
              <Route path="inbox" element={<VoiceInbox />} />
            </Route>
            
            {/* Main Website with Header/Footer */}
            <Route path="/*" element={
              <>
                <Header setIsPlayerVisible={setIsPlayerVisible} />
                <main className="flex-1">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/listen-live" element={<ListenLive />} />
                    <Route path="/programs" element={<Programs />} />
                    <Route path="/impact" element={<Impact />} />
                    <Route path="/donate" element={<Donate />} />
              <Route path="/donate/major-gifts" element={<MajorGiftsPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/reports/community-foundation" element={<CommunityFoundationReport />} />
              <Route path="/programs-lineup" element={<ProgramsLineup />} />
              <Route path="/church-partners" element={<ChurchPartners />} />
              <Route path="/media" element={<MediaGallery />} />
              <Route path="/volunteer" element={<Volunteer />} />
              <Route path="/visitors" element={<Visitors />} />
              <Route path="/donations" element={<Donations />} />
              <Route path="/project-detail/:projectCode" element={<ProjectDetail />} />
              <Route path="/program-assistant" element={<ProgramAssistant />} />
              <Route path="/ai" element={<ProgramAssistant />} />
              <Route path="/crm" element={<CRM />} />
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/admin-dashboard" element={<Dashboard />} />
              <Route path="/dashboard" element={<PresentersDashboard />} />
              <Route path="/kioo-presenters-dashboard-1981" element={<PresentersDashboard />} />
              <Route path="/launch" element={<Launch />} />
              <Route path="/clocks" element={<Clocks />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/samples" element={<Podcast />} />
              <Route path="/weather-dashboard" element={<WeatherDashboard />} />
              <Route path="/reporting/login" element={<ReportingLogin />} />
              <Route path="/reporting/dashboard" element={<ReportingDashboard />} />
              <Route path="/reporting/editorial" element={<EditorialDashboard />} />
              <Route path="/news" element={<ReportingNews />} />
              <Route path="/family" element={<FamilySignup />} />
              <Route path="/a-minute-with-god" element={<AMinuteWithGod />} />
              <Route path="/a-minute-with-god/:language" element={<AMinuteWithGodLanguage />} />
              <Route path="/givenow" element={<GiveNow />} />
              <Route path="/staff" element={<Staff />} />
              <Route path="/impact-stories" element={<ImpactStories />} />
              <Route path="/kioocrm" element={<KiooCRMHub />} />
              <Route path="/kissi-learn" element={<KissiLearn />} />
                  </Routes>
                </main>

                <Footer />

                {/* Contact Popup */}
                <ContactPopup 
                  isOpen={isContactPopupOpen} 
                  onClose={() => setIsContactPopupOpen(false)} 
                />
              </>
            } />
          </Routes>

          {/*
            Global Audio Player — rendered OUTSIDE all <Routes> so it is
            mounted exactly once for the lifetime of the app and keeps
            playing across every page navigation (per user requirement).
          */}
          <AudioPlayer
            isVisible={isPlayerVisible}
            onClose={() => setIsPlayerVisible(false)}
          />
        </Router>
        </div>
      </TranslationProvider>
    </HelmetProvider>
  );
}

export default App;